import { AlertTriangle, Check, Minus, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { DataTable, type DataColumn } from '@/components/ui/DataTable'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fmtMoney, fmtPct } from '@/lib/format'

/** GET /accounting/plan-fact → `lines` (docs/accounting.md «План-факт»). */
export interface PlanFactLine {
  key: string
  label: string
  kind: 'revenue' | 'expense'
  plan_rub: string | null
  fact_rub: string
  deviation_rub: string | null
  deviation_pct: string | null
  /** ok | warn | over | none */
  state: string
  state_label: string
}

export interface PlanFact {
  period: string
  label: string
  months: string[]
  closed_months: string[]
  lines: PlanFactLine[]
}

const STATE: Record<string, { tone: 'ok' | 'warn' | 'danger' | 'neutral'; icon: ReactNode }> = {
  ok: { tone: 'ok', icon: <Check /> },
  warn: { tone: 'warn', icon: <AlertTriangle /> },
  over: { tone: 'danger', icon: <X /> },
  none: { tone: 'neutral', icon: <Minus /> },
}

/** «+5,00 %» / «−12,40 %» / «—» (no plan or a zero plan). */
function deviationPct(v: string | null): string {
  if (v === null) return '—'
  return Number(v) > 0 ? `+${fmtPct(v)}` : fmtPct(v)
}

const COLUMNS: DataColumn<PlanFactLine>[] = [
  {
    id: 'line',
    header: 'Статья',
    cell: (l) => <span className={l.key === 'expenses_total' ? 'font-semibold text-ink-1' : 'text-ink-1'}>{l.label}</span>,
    mobile: 'title',
  },
  { id: 'plan', header: 'План', type: 'money', unit: '₽', className: 'text-ink-2', cell: (l) => (l.plan_rub === null ? '—' : fmtMoney(l.plan_rub)), mobile: 'field' },
  { id: 'fact', header: 'Факт', type: 'money', unit: '₽', cell: (l) => fmtMoney(l.fact_rub), mobile: 'amount' },
  {
    id: 'dev',
    header: 'Отклонение',
    type: 'money',
    unit: '₽',
    className: 'text-ink-2',
    cell: (l) => (l.deviation_rub === null ? '—' : fmtMoney(l.deviation_rub)),
    mobile: 'field',
  },
  { id: 'pct', header: 'Отклонение, %', type: 'percent', className: 'text-ink-2', cell: (l) => deviationPct(l.deviation_pct), mobile: 'meta' },
  {
    id: 'state',
    header: 'Состояние',
    type: 'status',
    cell: (l) => {
      const st = STATE[l.state] ?? STATE.none
      return (
        <StatusBadge tone={st?.tone ?? 'neutral'} icon={st?.icon}>
          {l.state_label}
        </StatusBadge>
      )
    },
    mobile: 'status',
  },
]

/**
 * «План-факт»: the revenue target and every expense category of a month or a quarter — plan,
 * fact, deviation in ₽ and %, and the state in colour + icon + word (rule 21). Money is right
 * aligned with tabular digits (the DataTable money column). Expenses: within plan «В бюджете»,
 * up to +10 % «Превышение до 10 %», more «Перерасход»; revenue: «План выполнен» / «Почти
 * выполнен» (≥ 90 %) / «Ниже плана»; no plan — «Нет плана».
 */
export function PlanFactTable({
  data,
  error,
  onRetry,
}: {
  /** undefined while loading */
  data?: PlanFact
  error?: unknown
  onRetry?: () => void
}) {
  if (data === undefined) {
    if (error) return <ErrorState title="Не удалось загрузить план-факт" error={error} onRetry={onRetry} />
    return (
      <div className="flex flex-col gap-2" role="status" aria-label="Загрузка">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-2/3" />
      </div>
    )
  }
  const planned = data.lines.some((l) => l.plan_rub !== null)
  return (
    <div className="flex flex-col gap-2">
      {!planned && (
        <EmptyState title="План на период не задан" text="Внесите бюджет по статьям и цель по выручке ниже — факт уже посчитан." />
      )}
      <DataTable label={`План-факт: ${data.label}`} rows={data.lines} columns={COLUMNS} getRowId={(l) => l.key} />
      {data.closed_months.length > 0 && (
        <p className="text-caption text-ink-3">Факт закрытых месяцев — снимок на момент закрытия.</p>
      )}
    </div>
  )
}
